/**
 * LoginScreen - Magic Link Authentication
 *
 * Simple, passwordless sign-in using email magic links.
 * Matches the SuperJarvis design language.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { SpaceBackground, AtomIcon, GlassCard } from './shared';

export function LoginScreen() {
  const navigate = useNavigate();
  const { step, sentToEmail, loading, error, sendLink, completeSignIn, resetFlow } = useAuthContext();
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      await sendLink(email.trim());
    }
  };

  const handleCompleteSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmEmail.trim()) {
      await completeSignIn(confirmEmail.trim());
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4">
        {/* Logo */}
        <div className="flex items-center space-x-4 mb-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
          <AtomIcon className="w-16 h-16 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
          <div className="flex flex-col">
            <h1 className="text-4xl font-black tracking-tighter text-cyan-400 drop-shadow-[0_0_20px_rgba(34,211,238,0.4)]">
              SUPERJARVIS
            </h1>
            <p className="text-cyan-400/60 text-xs tracking-[0.2em] uppercase">
              Executive AI Companion
            </p>
          </div>
        </div>

        <GlassCard className="w-full max-w-md p-8">
          {/* Idle State - Enter Email */}
          {step === 'idle' && (
            <form onSubmit={handleSendLink} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-cyan-300 mb-2">Welcome</h2>
                <p className="text-cyan-400/60 text-sm">Sign in with your email to continue</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-cyan-400/70 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black/50 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/30 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  disabled={loading}
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center py-2 px-4 bg-red-400/10 border border-red-400/30 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className={`w-full py-3 rounded-xl font-bold tracking-wider uppercase text-sm transition-all duration-300 ${
                  loading || !email.trim()
                    ? 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                    : 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_25px_rgba(34,211,238,0.4)] hover:shadow-[0_0_40px_rgba(34,211,238,0.6)]'
                }`}
              >
                {loading ? 'Sending...' : 'Send Magic Link'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full py-2 text-cyan-400/60 hover:text-cyan-400 text-sm transition-colors"
              >
                ← Back to Home
              </button>
            </form>
          )}

          {/* Email Sent State */}
          {step === 'email-sent' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-cyan-400/10 border border-cyan-400/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-cyan-300 mb-2">Check Your Email</h2>
                <p className="text-cyan-400/60 text-sm">
                  We sent a magic link to<br />
                  <span className="text-cyan-400 font-semibold">{sentToEmail}</span>
                </p>
              </div>

              <p className="text-cyan-400/40 text-xs">
                Click the link in your email to sign in. The link expires in 1 hour.
              </p>

              <button
                onClick={resetFlow}
                className="w-full py-2 text-cyan-400/60 hover:text-cyan-400 text-sm transition-colors"
              >
                Use a different email
              </button>
            </div>
          )}

          {/* Needs Email State - User opened link on different device */}
          {step === 'needs-email' && (
            <form onSubmit={handleCompleteSignIn} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-cyan-300 mb-2">Confirm Your Email</h2>
                <p className="text-cyan-400/60 text-sm">
                  Please enter the email address you used to sign in
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-cyan-400/70 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black/50 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/30 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  disabled={loading}
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center py-2 px-4 bg-red-400/10 border border-red-400/30 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !confirmEmail.trim()}
                className={`w-full py-3 rounded-xl font-bold tracking-wider uppercase text-sm transition-all duration-300 ${
                  loading || !confirmEmail.trim()
                    ? 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                    : 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_25px_rgba(34,211,238,0.4)] hover:shadow-[0_0_40px_rgba(34,211,238,0.6)]'
                }`}
              >
                {loading ? 'Signing In...' : 'Complete Sign In'}
              </button>
            </form>
          )}

          {/* Completing State */}
          {step === 'completing' && (
            <div className="text-center space-y-6 py-8">
              <div className="w-12 h-12 mx-auto border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
              <p className="text-cyan-400/70 text-sm tracking-wider">Signing you in...</p>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
